import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from "react";
import { Box, Typography, Button, IconButton, CircularProgress, Chip, Tooltip, Divider, Collapse, TextField, Snackbar, Alert, } from "@mui/material";
import { styled } from "@mui/material/styles";
import { FaTrash, FaPlus, FaChevronDown, FaChevronRight, FaEdit, FaCheck, FaTimes, } from "react-icons/fa";
import FilePreview from "./FilePreview";
const TagsContainer = styled(Box)(({ theme }) => ({
    width: "85%",
    padding: 0,
    paddingLeft: "50px",
    marginBottom: theme.spacing(2),
}));
const SubjectSection = styled(Box)(({ theme }) => ({
    border: "1px solid #D5DDE4",
    borderRadius: "8px",
    marginBottom: theme.spacing(2),
    overflow: "hidden",
}));
const SubjectHeader = styled(Box)(({ theme }) => ({
    backgroundColor: "#0E497A",
    color: "white",
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    userSelect: "none",
}));
const SubjectContent = styled(Box)(({ theme }) => ({
    backgroundColor: "white",
    padding: "16px",
}));
const TopicItem = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    borderLeft: "2px solid #E8F4FD",
}));
const TopicLabel = styled(Typography)(({ theme }) => ({
    color: "#0E497A",
    fontWeight: 600,
    fontSize: "14px",
    fontFamily: "IBM Plex Sans, sans-serif",
    marginBottom: theme.spacing(1),
}));
const SubtopicSection = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(1),
    paddingLeft: theme.spacing(2),
}));
const SubtopicLabel = styled(Typography)(({ theme }) => ({
    color: "#0E497A",
    fontWeight: 500,
    fontSize: "13px",
    fontFamily: "IBM Plex Sans, sans-serif",
    marginBottom: theme.spacing(1),
}));
const ChipContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
}));
const AddSubtopicContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
}));
const AddItemContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));
const ContextContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));
const SubmitButton = styled(Button)(({ theme }) => ({
    color: "#0E497A",
    borderColor: "#0E497A",
    backgroundColor: "#fff",
    fontFamily: "IBM Plex Sans, sans-serif",
    borderRadius: "25px",
    padding: "8px 24px",
    textTransform: "none",
    fontWeight: 500,
    "&:hover": {
        borderColor: "#0E497A",
        backgroundColor: "rgba(14, 73, 122, 0.04)",
    },
    "&.Mui-disabled": {
        color: "#9e9e9e",
        borderColor: "#e0e0e0",
        backgroundColor: "#f5f5f5",
    },
}));
const LoadingOverlay = styled(Box)(({ theme }) => ({
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    zIndex: 1000,
}));
const StyledTextarea = styled("textarea")(({ theme, isSubmitted }) => ({
    flexGrow: 1,
    borderRadius: "4px",
    border: `1px solid ${isSubmitted ? "#0E497A" : "#D5DDE4"}`,
    "&:hover": {
        borderColor: "#0E497A",
    },
    "&:focus": {
        borderColor: "#0E497A",
        outline: "none",
    },
    padding: theme.spacing(1),
    fontFamily: "IBM Plex Sans, sans-serif",
    fontSize: "0.85rem",
    minHeight: "60px",
    resize: "vertical",
    backgroundColor: isSubmitted ? "#0E497A" : "transparent",
    color: isSubmitted ? "#fff" : "#0E497A",
    "&:disabled": {
        backgroundColor: "#0E497A",
        color: "#fff",
        borderColor: "#0E497A",
    },
}));
const StyledTextField = styled(TextField)(({ theme }) => ({
    "& .MuiOutlinedInput-root": {
        borderRadius: "4px",
        fontSize: "13px",
        "& fieldset": {
            borderColor: "#D5DDE4",
        },
        "&:hover fieldset": {
            borderColor: "#0E497A",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#0E497A",
        },
    },
    "& .MuiInputBase-input": {
        fontFamily: "IBM Plex Sans, sans-serif",
        padding: "8px 12px",
    },
}));
const Tags = ({ tags, onTagsChange, showTags = true, onAction, summary, fileData, onFileRemove, }) => {
    const [localTags, setLocalTags] = useState(tags);
    const [additionalContext, setAdditionalContext] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [expandedSubjects, setExpandedSubjects] = useState(
    // Initialize all subjects as expanded
    Object.fromEntries(tags.subject_tags.map((_, index) => [index, true])));
    const [newSubtopicInputs, setNewSubtopicInputs] = useState({});
    const [newSubjectInput, setNewSubjectInput] = useState("");
    const [newTopicInputs, setNewTopicInputs] = useState({});
    const [editingSubject, setEditingSubject] = useState(null);
    const [editingSubjectValue, setEditingSubjectValue] = useState("");
    const [editingTopic, setEditingTopic] = useState(null);
    const [editingTopicValue, setEditingTopicValue] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    if (!showTags)
        return null;
    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };
    const toggleSubject = (index) => {
        if (isSubmitted)
            return;
        setExpandedSubjects((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    };
    // SUBJECT CRUD OPERATIONS
    const handleAddSubject = () => {
        if (isSubmitted || !newSubjectInput.trim())
            return;
        const newTags = { ...localTags };
        newTags.subject_tags.push({
            subject: newSubjectInput.trim(),
            topic_tags: [],
        });
        setLocalTags(newTags);
        onTagsChange?.(newTags);
        setNewSubjectInput("");
        // Expand the newly added subject
        setExpandedSubjects((prev) => ({
            ...prev,
            [newTags.subject_tags.length - 1]: true,
        }));
    };
    const handleRemoveSubject = (subjectIndex) => {
        if (isSubmitted)
            return;
        if (localTags.subject_tags.length <= 1) {
            setSnackbarMessage("At least one subject must be present");
            setSnackbarOpen(true);
            return;
        }
        const newTags = { ...localTags };
        newTags.subject_tags.splice(subjectIndex, 1);
        setLocalTags(newTags);
        onTagsChange?.(newTags);
    };
    const handleStartEditSubject = (subjectIndex, currentValue) => {
        if (isSubmitted)
            return;
        setEditingSubject(subjectIndex);
        setEditingSubjectValue(currentValue);
    };
    const handleSaveEditSubject = (subjectIndex) => {
        if (!editingSubjectValue.trim()) {
            setSnackbarMessage("Subject cannot be empty");
            setSnackbarOpen(true);
            return;
        }
        const newTags = { ...localTags };
        newTags.subject_tags[subjectIndex].subject = editingSubjectValue.trim();
        setLocalTags(newTags);
        onTagsChange?.(newTags);
        setEditingSubject(null);
        setEditingSubjectValue("");
    };
    const handleCancelEditSubject = () => {
        setEditingSubject(null);
        setEditingSubjectValue("");
    };
    // TOPIC CRUD OPERATIONS
    const handleAddTopic = (subjectIndex) => {
        if (isSubmitted)
            return;
        const topicValue = newTopicInputs[subjectIndex]?.trim();
        if (!topicValue)
            return;
        const newTags = { ...localTags };
        newTags.subject_tags[subjectIndex].topic_tags.push({
            topic: topicValue,
            subtopic_tags: [],
        });
        setLocalTags(newTags);
        onTagsChange?.(newTags);
        // Clear the input
        setNewTopicInputs((prev) => ({
            ...prev,
            [subjectIndex]: "",
        }));
    };
    const handleRemoveTopic = (subjectIndex, topicIndex) => {
        if (isSubmitted)
            return;
        if (localTags.subject_tags[subjectIndex].topic_tags.length <= 1) {
            setSnackbarMessage("At least one topic must be present for this subject");
            setSnackbarOpen(true);
            return;
        }
        const newTags = { ...localTags };
        newTags.subject_tags[subjectIndex].topic_tags.splice(topicIndex, 1);
        setLocalTags(newTags);
        onTagsChange?.(newTags);
    };
    const handleStartEditTopic = (subjectIndex, topicIndex, currentValue) => {
        if (isSubmitted)
            return;
        setEditingTopic(`${subjectIndex}-${topicIndex}`);
        setEditingTopicValue(currentValue);
    };
    const handleSaveEditTopic = (subjectIndex, topicIndex) => {
        if (!editingTopicValue.trim()) {
            setSnackbarMessage("Topic cannot be empty");
            setSnackbarOpen(true);
            return;
        }
        const newTags = { ...localTags };
        newTags.subject_tags[subjectIndex].topic_tags[topicIndex].topic =
            editingTopicValue.trim();
        setLocalTags(newTags);
        onTagsChange?.(newTags);
        setEditingTopic(null);
        setEditingTopicValue("");
    };
    const handleCancelEditTopic = () => {
        setEditingTopic(null);
        setEditingTopicValue("");
    };
    const handleTopicInputChange = (subjectIndex, value) => {
        setNewTopicInputs((prev) => ({
            ...prev,
            [subjectIndex]: value,
        }));
    };
    // SUBTOPIC CRUD OPERATIONS
    const handleRemoveSubtopic = (subjectIndex, topicIndex, subtopicIndex) => {
        if (isSubmitted)
            return;
        const newTags = { ...localTags };
        newTags.subject_tags[subjectIndex].topic_tags[topicIndex].subtopic_tags.splice(subtopicIndex, 1);
        setLocalTags(newTags);
        onTagsChange?.(newTags);
    };
    const handleAddSubtopic = (subjectIndex, topicIndex) => {
        if (isSubmitted)
            return;
        const key = `${subjectIndex}-${topicIndex}`;
        const subtopic = newSubtopicInputs[key]?.trim();
        if (!subtopic)
            return;
        const newTags = { ...localTags };
        if (!newTags.subject_tags[subjectIndex].topic_tags[topicIndex].subtopic_tags.includes(subtopic)) {
            newTags.subject_tags[subjectIndex].topic_tags[topicIndex].subtopic_tags.push(subtopic);
            setLocalTags(newTags);
            onTagsChange?.(newTags);
            // Clear the input
            setNewSubtopicInputs((prev) => ({
                ...prev,
                [key]: "",
            }));
        }
    };
    const handleSubtopicInputChange = (subjectIndex, topicIndex, value) => {
        const key = `${subjectIndex}-${topicIndex}`;
        setNewSubtopicInputs((prev) => ({
            ...prev,
            [key]: value,
        }));
    };
    const handleRefresh = () => {
        if (isSubmitted)
            return;
        setIsSubmitted(true);
        setTimeout(() => {
            onAction?.({
                action: "refresh",
                tags: localTags,
                additionalContext: summary
                    ? `Context provided by the user: ${additionalContext}, Here is the document summary: ${summary}`
                    : additionalContext,
                message: {
                    content: "Refreshing tags...",
                    role: "load",
                },
            });
        }, 100);
    };
    const handleSubmit = () => {
        for (const sub of localTags.subject_tags) {
            if (!sub.subject.trim()) {
                setSnackbarMessage("Subject cannot be empty");
                setSnackbarOpen(true);
                return;
            }
            for (const t of sub.topic_tags) {
                if (!t.topic.trim()) {
                    setSnackbarMessage("Topic cannot be empty");
                    setSnackbarOpen(true);
                    return;
                }
            }
        }
        setIsSubmitted(true);
        setTimeout(() => {
            onAction?.({
                action: "submit",
                tags: localTags,
                additionalContext: summary
                    ? `Context provided by the user: ${additionalContext}, Here is the document summary: ${summary}`
                    : additionalContext,
                message: {
                    content: "Tags uploaded successfully",
                    role: "assistant",
                },
            });
        }, 100);
    };
    const renderFilePreview = () => {
        if (!fileData || fileData.size === 0)
            return null;
        return (_jsx("div", { style: {
                position: "relative",
                marginTop: "32px",
                marginBottom: "32px",
            }, children: _jsx(FilePreview, { fileData: fileData, onClose: () => {
                    if (onFileRemove)
                        onFileRemove();
                }, isCurrent: true, messages: [] }) }));
    };
    return (_jsxs(TagsContainer, { children: [_jsx("br", {}), renderFilePreview(), _jsx("br", {}), isLoading && (_jsx(LoadingOverlay, { children: _jsx(CircularProgress, {}) })), _jsx(Snackbar, { open: snackbarOpen, autoHideDuration: 3000, onClose: handleCloseSnackbar, anchorOrigin: { vertical: "bottom", horizontal: "center" }, children: _jsx(Alert, { onClose: handleCloseSnackbar, severity: "warning", sx: { width: "100%" }, children: snackbarMessage }) }), localTags.subject_tags.map((tag, subjectIndex) => (_jsxs(SubjectSection, { children: [_jsxs(SubjectHeader, { onClick: () => toggleSubject(subjectIndex), children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1, flex: 1 }, children: [expandedSubjects[subjectIndex] ? (_jsx(FaChevronDown, { size: 14 })) : (_jsx(FaChevronRight, { size: 14 })), editingSubject === subjectIndex ? (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1, flex: 1 }, onClick: (e) => e.stopPropagation(), children: [_jsx(StyledTextField, { size: "small", value: editingSubjectValue, onChange: (e) => setEditingSubjectValue(e.target.value), onKeyDown: (e) => {
                                                    if (e.key === "Enter") {
                                                        handleSaveEditSubject(subjectIndex);
                                                    }
                                                    else if (e.key === "Escape") {
                                                        handleCancelEditSubject();
                                                    }
                                                }, autoFocus: true, sx: {
                                                    flex: 1,
                                                    "& .MuiOutlinedInput-root": {
                                                        backgroundColor: "white",
                                                        "& fieldset": {
                                                            borderColor: "white",
                                                        },
                                                    },
                                                } }), _jsx(IconButton, { size: "small", onClick: () => handleSaveEditSubject(subjectIndex), sx: {
                                                    color: "white",
                                                    "&:hover": {
                                                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                                                    },
                                                }, children: _jsx(FaCheck, { size: 14 }) }), _jsx(IconButton, { size: "small", onClick: handleCancelEditSubject, sx: {
                                                    color: "white",
                                                    "&:hover": {
                                                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                                                    },
                                                }, children: _jsx(FaTimes, { size: 14 }) })] })) : (_jsxs(Typography, { sx: {
                                            fontFamily: "IBM Plex Sans, sans-serif",
                                            fontWeight: 600,
                                        }, children: ["Subject: ", tag.subject] }))] }), _jsxs(Box, { sx: { display: "flex", gap: 0.5 }, children: [!isSubmitted && editingSubject !== subjectIndex && (_jsx(Tooltip, { title: "Edit Subject", children: _jsx(IconButton, { size: "small", onClick: (e) => {
                                                e.stopPropagation();
                                                handleStartEditSubject(subjectIndex, tag.subject);
                                            }, sx: {
                                                color: "rgba(255, 255, 255, 0.8)",
                                                "&:hover": {
                                                    color: "white",
                                                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                                                },
                                            }, children: _jsx(FaEdit, { size: 14 }) }) })), _jsx(Tooltip, { title: "Remove Subject", children: _jsx(IconButton, { size: "small", onClick: (e) => {
                                                e.stopPropagation();
                                                handleRemoveSubject(subjectIndex);
                                            }, sx: {
                                                color: "rgba(255, 255, 255, 0.8)",
                                                "&:hover": {
                                                    color: "white",
                                                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                                                },
                                            }, disabled: isSubmitted, children: _jsx(FaTrash, { size: 14 }) }) })] })] }), _jsx(Collapse, { in: expandedSubjects[subjectIndex], children: _jsxs(SubjectContent, { children: [tag.topic_tags.map((topic, topicIndex) => (_jsxs(TopicItem, { children: [_jsx(Box, { sx: {
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                mb: 1,
                                            }, children: editingTopic === `${subjectIndex}-${topicIndex}` ? (_jsxs(Box, { sx: {
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                    flex: 1,
                                                }, children: [_jsx(StyledTextField, { size: "small", value: editingTopicValue, onChange: (e) => setEditingTopicValue(e.target.value), onKeyDown: (e) => {
                                                            if (e.key === "Enter") {
                                                                handleSaveEditTopic(subjectIndex, topicIndex);
                                                            }
                                                            else if (e.key === "Escape") {
                                                                handleCancelEditTopic();
                                                            }
                                                        }, autoFocus: true, sx: { flex: 1 } }), _jsx(IconButton, { size: "small", onClick: () => handleSaveEditTopic(subjectIndex, topicIndex), sx: {
                                                            color: "#0E497A",
                                                            "&:hover": {
                                                                backgroundColor: "rgba(14, 73, 122, 0.04)",
                                                            },
                                                        }, children: _jsx(FaCheck, { size: 14 }) }), _jsx(IconButton, { size: "small", onClick: handleCancelEditTopic, sx: {
                                                            color: "#0E497A",
                                                            "&:hover": {
                                                                backgroundColor: "rgba(14, 73, 122, 0.04)",
                                                            },
                                                        }, children: _jsx(FaTimes, { size: 14 }) })] })) : (_jsxs(_Fragment, { children: [_jsxs(TopicLabel, { children: ["Topic: ", topic.topic] }), !isSubmitted && (_jsxs(Box, { sx: { display: "flex", gap: 0.5 }, children: [_jsx(Tooltip, { title: "Edit Topic", children: _jsx(IconButton, { size: "small", onClick: () => handleStartEditTopic(subjectIndex, topicIndex, topic.topic), sx: {
                                                                        color: "#0E497A",
                                                                        "&:hover": {
                                                                            backgroundColor: "rgba(14, 73, 122, 0.04)",
                                                                        },
                                                                    }, children: _jsx(FaEdit, { size: 14 }) }) }), _jsx(Tooltip, { title: "Remove Topic", children: _jsx(IconButton, { size: "small", onClick: () => handleRemoveTopic(subjectIndex, topicIndex), sx: {
                                                                        color: "#d32f2f",
                                                                        "&:hover": {
                                                                            backgroundColor: "rgba(211, 47, 47, 0.04)",
                                                                        },
                                                                    }, children: _jsx(FaTrash, { size: 14 }) }) })] }))] })) }), _jsxs(SubtopicSection, { children: [_jsx(SubtopicLabel, { children: "Sub-Topic" }), _jsx(ChipContainer, { children: topic.subtopic_tags.map((subtopic, subtopicIndex) => (_jsx(Chip, { label: subtopic, onDelete: isSubmitted
                                                            ? undefined
                                                            : () => handleRemoveSubtopic(subjectIndex, topicIndex, subtopicIndex), size: "small", sx: {
                                                            color: "#0E497A",
                                                            border: "1px solid #d5dde4",
                                                            backgroundColor: "transparent",
                                                            "& .MuiChip-deleteIcon": {
                                                                color: "#0E497A",
                                                                "&:hover": {
                                                                    color: "#d32f2f",
                                                                },
                                                            },
                                                        }, disabled: isSubmitted }, subtopicIndex))) }), !isSubmitted && (_jsxs(AddSubtopicContainer, { children: [_jsx(StyledTextField, { size: "small", placeholder: "Add subtopic", value: newSubtopicInputs[`${subjectIndex}-${topicIndex}`] || "", onChange: (e) => handleSubtopicInputChange(subjectIndex, topicIndex, e.target.value), onKeyDown: (e) => {
                                                                if (e.key === "Enter") {
                                                                    handleAddSubtopic(subjectIndex, topicIndex);
                                                                }
                                                            }, sx: { flexGrow: 1 } }), _jsx(Tooltip, { title: "Add Subtopic", children: _jsx(IconButton, { size: "small", onClick: () => handleAddSubtopic(subjectIndex, topicIndex), sx: {
                                                                    color: "#0E497A",
                                                                    "&:hover": {
                                                                        backgroundColor: "rgba(14, 73, 122, 0.04)",
                                                                    },
                                                                }, children: _jsx(FaPlus, { size: 14 }) }) })] }))] })] }, topicIndex))), !isSubmitted && (_jsxs(AddItemContainer, { children: [_jsx(StyledTextField, { size: "small", placeholder: "Add new topic", value: newTopicInputs[subjectIndex] || "", onChange: (e) => handleTopicInputChange(subjectIndex, e.target.value), onKeyDown: (e) => {
                                                if (e.key === "Enter") {
                                                    handleAddTopic(subjectIndex);
                                                }
                                            }, sx: { flexGrow: 1 } }), _jsx(Tooltip, { title: "Add Topic", children: _jsx(IconButton, { size: "small", onClick: () => handleAddTopic(subjectIndex), sx: {
                                                    color: "#0E497A",
                                                    "&:hover": {
                                                        backgroundColor: "rgba(14, 73, 122, 0.04)",
                                                    },
                                                }, children: _jsx(FaPlus, { size: 16 }) }) })] }))] }) })] }, subjectIndex))), !isSubmitted && (_jsxs(AddItemContainer, { children: [_jsx(StyledTextField, { size: "small", placeholder: "Add new subject", value: newSubjectInput, onChange: (e) => setNewSubjectInput(e.target.value), onKeyDown: (e) => {
                            if (e.key === "Enter") {
                                handleAddSubject();
                            }
                        }, sx: { flexGrow: 1 } }), _jsx(Tooltip, { title: "Add Subject", children: _jsx(IconButton, { size: "small", onClick: handleAddSubject, sx: {
                                color: "#0E497A",
                                "&:hover": {
                                    backgroundColor: "rgba(14, 73, 122, 0.04)",
                                },
                            }, children: _jsx(FaPlus, { size: 16 }) }) })] })), _jsx(Divider, { sx: { my: 2 } }), _jsx(Box, { sx: { display: "flex", justifyContent: "flex-end" }, children: _jsx(SubmitButton, { variant: "contained", onClick: handleSubmit, disabled: isSubmitted, children: "Next" }) })] }));
};
export default Tags;
